// src/features/records/components/shared/RecordActions.js
import React from 'react';
import { Heart, MessageCircle, Link, Share2, Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './RecordActions.module.css';
import { useToast } from '../../../../hooks/useToast';
import { useAuth } from '../../../../contexts/AuthContext';
import { shareRecordLink } from '../../utils/shareUtils';
import useRecordReaction from '../../hooks/useRecordReaction';
import useRecordCommentCount from '../../hooks/useRecordCommentCount';
import useRecordMark from '../../hooks/useRecordMark';

const RecordActions = ({ recordId, onLinkToRecord, onComment }) => {
  const { t } = useTranslation();
  const { uid: currentUserId } = useAuth();
  const { count: commentCount } = useRecordCommentCount(recordId);
  const { marked, toggle: toggleMark, loading: markLoading } = useRecordMark(recordId);

  const { liked, count: likeCount, isToggling, toggle: toggleLike } = useRecordReaction(recordId, 'e:2764', {
    userId: currentUserId,
  });

  const toast = useToast();

  const handleLike = async (e) => {
    e.stopPropagation();

    try {
      await toggleLike();
    } catch (error) {
      toast.error(t('records.errors.like_update_failed', 'Unable to update like right now. Please try again.'));
    }
  };

  const handleComment = (e) => {
    e.stopPropagation();
    if (onComment) {
      onComment();
      return;
    }
    const target = document.getElementById(`record-comments-${recordId}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const composer = target.querySelector('[data-comment-composer="root"]');
      if (composer) {
        composer.focus();
      }
    }
  };

  const trimmedRecordId = typeof recordId === 'string' ? recordId.trim() : '';
  const canLink = typeof onLinkToRecord === 'function' && Boolean(trimmedRecordId);

  const handleLink = (e) => {
    e.stopPropagation();
    if (!canLink) {
      return;
    }
    onLinkToRecord(trimmedRecordId);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    try {
      const result = await shareRecordLink(recordId);
      if (result.action === 'copied' && result.success) {
        toast.success(t('records.actions.link_copied', 'Record link copied to clipboard'));
      } else if (result.action === 'shared' && result.success) {
        // Mobile share sheet invoked; no toast.
        return;
      } else if (result.action === 'aborted') {
        // User dismissed share sheet; no toast needed.
        return;
      } else {
        toast.error(t('records.errors.share_failed', 'Unable to share this record right now. Please try again.'));
      }
    } catch (error) {
      console.error('Failed to share record link:', error);
      toast.error(t('records.errors.share_failed', 'Unable to share this record right now. Please try again.'));
    }
  };

  const handleMark = async (e) => {
    e.stopPropagation();
    try {
      await toggleMark();
    } catch (error) {
      console.error('Failed to update mark:', error);
      toast.error(t('records.errors.mark_update_failed', 'Unable to update mark right now. Please try again.'));
    }
  };

  return (
    <div className={styles.recordActions}>
      <div className={styles.actionGroup}>
        <button 
          className={`${styles.actionBtn} ${styles.likeBtn} ${liked ? styles.liked : ''}`}
          onClick={handleLike}
          disabled={isToggling}
          aria-label={t('records.actions.like_record_aria', 'Like record')}
        >
          <Heart 
            className={styles.actionIcon} 
            size={20} 
            fill={liked ? 'currentColor' : 'none'}
            stroke={liked ? 'none' : 'currentColor'}
            strokeWidth={liked ? 0 : 2}
          />
          <span>{likeCount}</span>
        </button>
        
        <button
          className={`${styles.actionBtn} ${styles.commentBtn}`}
          onClick={handleComment}
          aria-label={t('records.actions.comment_record_aria', 'Open comments')}
        >
          <MessageCircle className={styles.actionIcon} size={20} />
          <span>{commentCount}</span>
        </button>
        
        <button
          className={`${styles.actionBtn} ${styles.linkBtn}`}
          onClick={handleLink}
          disabled={!canLink}
          aria-disabled={!canLink}
          aria-label={t('records.actions.link_record_aria', 'Link record')}
        >
          <Link className={styles.actionIcon} size={20} />
        </button>
      </div>
      
      <div className={styles.actionGroup}>
        <button
          className={`${styles.actionBtn} ${styles.shareBtn}`}
          onClick={handleShare}
          aria-label={t('records.actions.share_record_aria', 'Share record')}
        >
          <Share2 className={styles.actionIcon} size={20} />
        </button>
        
        <button 
          className={`${styles.actionBtn} ${styles.markBtn} ${marked ? styles.marked : ''}`}
          onClick={handleMark}
          disabled={markLoading}
          aria-label={marked
            ? t('records.actions.unmark_record_aria', 'Unmark record')
            : t('records.actions.mark_record_aria', 'Mark record')
          }
          aria-pressed={marked}
        >
          <Bookmark className={styles.actionIcon} size={20} />
        </button>
      </div>
    </div>
  );
};

export default RecordActions;
